import { Modal } from '@marriott/mi-ui-library';
import React, { useEffect, useRef, useCallback } from 'react';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { useNavContext } from '../../../organisms/NavigationContainer';
import { addSubDirectoryPrefix, getSubDirectoryLocale } from '../../../utils';
import { StyledMyTripsModal, StyledMyTripsModalCloseButton, StyledFocusSentinel } from './MyTripsModal.styles';

const getTripsTrigger = (): HTMLElement | null =>
  document.getElementById('utility-mytrips-trigger') ||
  document.querySelector<HTMLElement>('.utility-trips .utility-mytrips-button');

const getSignInTrigger = (): HTMLElement | null =>
  document.querySelector<HTMLElement>('.utility-sigin [data-testid="common-static-SignInDrawer"]');

const isValidFocusTarget = (element: HTMLElement | null): element is HTMLElement => {
  if (!element || !document.body.contains(element)) return false;
  if (element === document.body) return false;
  if (element.closest('[role="dialog"]')) return false;
  return element.matches('button, a[href], [tabindex]:not([tabindex="-1"])');
};

export const MyTripsModal = () => {
  const { isMyTripsModalOpen, setIsMyTripsModalOpen } = useNavContext();
  const { envVarsObject } = useClientEnvVarsStore();
  const locale = (envVarsObject?.['ACCEPT_LANGUAGE'] ?? '').replace('_', '-');
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const scrollPositionRef = useRef(0);
  const localePath = locale.toLowerCase() === 'en-us' ? '' : getSubDirectoryLocale(locale);
  const tripsOverlayPath = addSubDirectoryPrefix(`${localePath}/resLookupOverlay.mi?overlay=true`);
  const lockBodyScroll = useCallback(() => {
    scrollPositionRef.current = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPositionRef.current}px`;
    document.body.style.width = '100%';
  }, []);

  const unlockBodyScroll = useCallback(() => {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollPositionRef.current);
  }, []);

  const restoreFocus = useCallback(() => {
    const candidates = [getTripsTrigger(), previousFocusRef.current, getSignInTrigger()];
    const targetElement = candidates.find(isValidFocusTarget);
    if (!targetElement) return;

    setTimeout(() => {
      targetElement.focus({ preventScroll: true });
      targetElement.scrollIntoView({ block: 'nearest' });
    }, 0);
  }, []);

  const setClosed = useCallback(() => {
    setIsMyTripsModalOpen(false);
    unlockBodyScroll();
  }, [setIsMyTripsModalOpen, unlockBodyScroll]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setClosed();
        return;
      }

      if (event.key !== 'Tab') return;

      const isCloseButtonFocused = document.activeElement === closeButtonRef.current;
      const isIframeFocused = document.activeElement === iframeRef.current;
      const modalContainer = document.getElementById('mytripsmodal');
      const isModalContainerFocused = document.activeElement === modalContainer;

      if (!event.shiftKey) {
        if (isModalContainerFocused) {
          event.preventDefault();
          event.stopPropagation();
          closeButtonRef.current?.focus();
        } else if (isIframeFocused) {
          event.preventDefault();
          event.stopPropagation();
          closeButtonRef.current?.focus();
        }
      } else {
        if (isCloseButtonFocused) {
          event.preventDefault();
          event.stopPropagation();
          iframeRef.current?.focus();
        } else if (isModalContainerFocused) {
          event.preventDefault();
          event.stopPropagation();
          iframeRef.current?.focus();
        }
      }
    },
    [setClosed]
  );
  const handleFocusIn = useCallback(() => {
    if (!isMyTripsModalOpen) return;
    const modalContainer = document.getElementById('mytripsmodal');
    if (!modalContainer) return;
    if (!modalContainer.contains(document.activeElement)) {
      closeButtonRef.current?.focus();
    }
  }, [isMyTripsModalOpen]);

  useEffect(() => {
    if (!isMyTripsModalOpen) return;

    previousFocusRef.current = getTripsTrigger() || (document.activeElement as HTMLElement);
    lockBodyScroll();
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const modalContainer = document.getElementById('mytripsmodal');
        modalContainer?.focus();
      }, 200);
    });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn);
      restoreFocus();
    };
  }, [isMyTripsModalOpen, handleKeyDown, handleFocusIn, lockBodyScroll, restoreFocus]);

  return (
    <StyledMyTripsModal
      modalId="mytripsmodal"
      show={isMyTripsModalOpen}
      handleBlur={true}
      popupOpenState={isMyTripsModalOpen}
      onClose={setClosed}
      className="my-trips-modal"
      secondaryClassName="my-trips-modal-inner"
      role="dialog"
      aria-modal={true}
      aria-label="Find Reservation"
    >
      <Modal.Body className="my-trips-modal-body">
        <StyledFocusSentinel tabIndex={0} onFocus={() => iframeRef.current?.focus()} aria-hidden="true" />
        <div>
          <StyledMyTripsModalCloseButton
            ref={closeButtonRef}
            className="icon-clear"
            onClick={setClosed}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setClosed();
              }
            }}
            type="button"
            aria-label="Close Dialog"
          >
            <span className="sr-only">Close Dialog</span>
          </StyledMyTripsModalCloseButton>
          <iframe
            ref={iframeRef}
            src={tripsOverlayPath}
            title="Find Reservation"
            frameBorder="0"
            allowFullScreen={false}
            id="resLookupOverlayIframe"
            width="100%"
            tabIndex={0}
            data-dt-last-src={`https://www.marriott.com${tripsOverlayPath}`}
            style={{ height: '500px' }}
          />
        </div>
        <StyledFocusSentinel tabIndex={0} onFocus={() => closeButtonRef.current?.focus()} aria-hidden="true" />
      </Modal.Body>
    </StyledMyTripsModal>
  );
};
